'use client'
import { memo, type MouseEvent, useMemo, useState, type FC, useCallback, useEffect } from 'react'
import { useParams, usePathname } from 'next/navigation'
import styled from 'styled-components'
import { motion, type Variants } from 'motion/react'
import { getIcon, LANG } from '@/utils'
import { useProduct } from '@/hooks'
import { getTransition, mq, presetsFluid, vw } from '@/styles'
import type { TypeCmsCategory } from '@/schema'
import { Img, Link } from '@/components'

type Props = {
  data: TypeCmsCategory[]
}

type PropsCategoryItem = {
  href: string
  id: TypeCmsCategory['id']
  name: TypeCmsCategory['name']
  children?: React.ReactNode
  subcategories?: TypeCmsCategory[]
  onClick?: (e: MouseEvent<HTMLElement>) => void
  isExpanded?: boolean,
  setIsExpanded?: (isExpanded: boolean) => void
}


const CateogyItem:FC<PropsCategoryItem> = memo(({ href, id, name, children, onClick, isExpanded, setIsExpanded }) => {
  const nameStr = typeof name === 'string' ? name : name[LANG]
  const pathname = usePathname()
  const isActive = (href && pathname) ? href === pathname : false

  return (
    <Li data-id={id} variants={itemVariants} onClick={onClick}>
      <Link href={href}{...{ className: `${isActive ? 'is-active' : ''}` }}>{nameStr}
      </Link>
      {children &&
        <Chevron onClick={() => {
          if (setIsExpanded) setIsExpanded(!isExpanded)
        }} aria-hidden={true} animate={{ rotate: isExpanded ? 90 : 0 }} transition={ChevronTransition}>
          <Img media={getIcon('chevron')} />
        </Chevron>
      }
      {children && (children)}
    </Li>
  )
})

const CateogyBlockItem:FC<PropsCategoryItem> = memo(({ href, id, name, subcategories }) => {
  const nameStr = typeof name === 'string' ? name : name[LANG]
  const [isExpanded, setIsExpanded] = useState(false)
  const { slug } = useParams()

  const onCLick = useCallback(() => {
    if(!isExpanded) setIsExpanded(true)
  }, [isExpanded, setIsExpanded])

  useEffect(() => {
    const slugInHref = typeof slug === 'string' ? href.includes(slug) : Array.isArray(slug) ? slug.some(s => href.includes(s)) : false
    setIsExpanded(slugInHref)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  return (
    <CateogyItem href={href} id={id} name={nameStr} isExpanded={isExpanded} setIsExpanded={setIsExpanded} onClick={onCLick}>
      <Ul aria-expanded={isExpanded} initial={'inactive'} animate={isExpanded ? 'active' : 'inactive'} exit={'exit'} variants={blockVariants}>
        {subcategories.map((subcategory, idx) => {
          const { id: subId, name: subName, slug } = subcategory
          const subHref = `/catalog/${typeof slug === 'string' ? slug : slug[LANG]}`
          const subNameStr = typeof subName === 'string' ? subName : subName[LANG]
          return <CateogyItem key={`${idx}-${subId}`} href={subHref} id={subId} name={subNameStr} onClick={onCLick} setIsExpanded={setIsExpanded} />
        })}
      </Ul>
    </CateogyItem>
  )
})

const getCategories = (data: Props['data']) => data.map((item, idx) => {
  const { id, name, slug, subcategories } = item
  const href = `/catalog/${typeof slug === 'string' ? slug : slug[LANG]}`

  if(!subcategories || subcategories.length === 0) return <CateogyItem key={`${idx}-${id}`} href={href} name={name} id={id} />

  return <CateogyBlockItem key={`${idx}-${id}`} href={href} name={name} id={id} subcategories={subcategories} />
})

export const SectionCategories:FC<Props> = memo(({ data }) => {
  const Categories = useMemo(() => getCategories(data), [data])
  const { isActiveProduct } = useProduct()

  return (
    <Section $compactedGrid={isActiveProduct}>
      <Ul $compactedGrid={isActiveProduct} initial='inactive' animate='active' exit='exit' variants={listVariants}>
        {Categories}
      </Ul>
    </Section>
  )
})

const Li = styled(motion.li)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  justify-content: space-between;
  position: relative;

  a {
    ${presetsFluid.font18_1B}
    align-items: center;
    color: var(--color-text--darker);
    display: flex;
    flex-direction: row;
    height: 45px;
    justify-content: space-between;
    opacity: .5;
    transition: 300ms color ease, 300ms opacity ease;
    width: 100%;

    &.is-active {
      color: var(--color-gold);
      opacity: 1;
    }
  }
`

const Ul = styled(motion.ul) <{ $compactedGrid?: boolean }>`
  display: none;

  &[aria-expanded='true'] {
    overflow: initial !important;
  }

  ${mq.greaterThan('tablet')} {
    display: block;
    transition: 200ms transform ease-in-out;
  }

  ${({ $compactedGrid }) => $compactedGrid && `
    transform: translateX(-25vw);
  `}

	${mq.greaterThan('desktop-wqhd')} {
		transform: translateX(0);
	}

  li {
    > ul {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      justify-content: space-between;

      > li {
        padding: 0 ${vw(20, 'mobile')};

        ${mq.greaterThan('tablet')} {
          padding: 0 ${vw(20, 'desktop')};
        }


        a {
          ${presetsFluid.font15_2B}
          opacity: .5;
          transition: 300ms opacity ease;

          &:hover {
            opacity: 1;
          }
        }

      }
    }
  }
`

const Section = styled.section<{ $compactedGrid?: boolean }>`
  grid-column: 1 / span 6;
  margin-bottom: 40px;
  -ms-overflow-style: none;  /* Internet Explorer 10+ */
  scrollbar-width: none;  /* Firefox, Safari 18.2+, Chromium 121+ */

  &::-webkit-scrollbar {
    display: none;
  }

  ${mq.greaterThan('tablet')} {
    grid-column: 1 / span 3;
    grid-row: 1 / span 1;
    overflow-x: hidden;
    max-height: calc(100vh - 62px);
    padding-bottom: 150px;
    pointer-events: ${({ $compactedGrid }) => $compactedGrid ? 'none' : 'auto'};
    position: sticky;
    top: 62px;
  }

	${mq.greaterThan('desktop')} {
		max-width: 320px;
	}

	${mq.greaterThan('desktop-wqhd')} {
		pointer-events: auto;
	}


  > ul {
    > li {
      > a {
        ${presetsFluid.font18}
        opacity: 1;
      }
    }
  }
`

const Chevron = styled(motion.button)`
  align-items: center;
  display: flex;
  height: 45px;
  position: absolute;
  justify-content: center;
  right: -20px;
  top: 0;
  width: 48px;

  img {
    height: ${vw(9, 'mobile')};
    width: ${vw(9, 'mobile')};

    ${mq.greaterThan('tablet')} {
      height: ${vw(9, 'desktop')};
      width: ${vw(9, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 9px;
      width: 9px;
    }
  }
`

const transition = {
  ...getTransition()
}

const listVariants:Variants = {
  inactive: {
    opacity: 0,
    transition
  },
  active: {
    opacity: 1,
    transition: {
      ...getTransition(),
      staggerChildren: .01
    }
  },
  exit: {
    opacity: 0,
    transition
  }
}

export const itemVariants:Variants = {
  inactive: {
    x: -10,
    opacity: 0,
    transition
  },
  active: {
    x: 0,
    opacity: 1,
    transition
  },
  exit: {
    opacity: 0
  }
}

export const blockVariants:Variants = {
  inactive: {
    height: 0,
    opacity: 0,
    pointerEvents: 'none',
    transitionEnd: {
      overflow: 'hidden'
    },
    transition: {
      ...getTransition()
    }
  },
  active: {
    height: 'auto',
    opacity: 1,
    pointerEvents: 'auto',
    transition: {
      ...getTransition(),
      staggerChildren: .01,
    }
  },
  exit: {
    height: 0,
    opacity: 0,
    pointerEvents: 'none',
    transitionEnd: {
      overflow: 'hidden'
    },
    transition: {
      ...getTransition(),
    }
  },
}

const ChevronTransition = {
  ease: [.25, .1, .25, 1],
  duration: .3,
  type: 'tween'
} as const
